import { useId, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { FaqCard } from '@/components/public/faq-section/faq-card/FaqCard';
import { TextAreaWithCharacterLimitGroup } from '@/components/admin/input-groups/text-area-with-character-limit-group/TextAreaWithCharacterLimitGroup';
import { Button } from '@/components/admin/button/Button';
import { SECTIONS_TEXT } from '@/const/admin/sections';
import { COMMON_TEXT_ADMIN } from '@/const/admin/common';
import { useSectionValidation } from '@/hooks/admin/use-section-validation';
import { PublishedFaqQuestion } from '@/types/public/faq-section';
import { FaqSectionQuestionDto } from '@/types/common/program-sections';
import { SectionTemplate, SectionMode } from '@/types/common/sections';
import { ContentType } from '@/types/common/section-contents';
import { getSectionTemplateMaxLength } from '@/utils/functions/section-template-validation/sectionTemplateValidation';
import { getTrimmedInputText } from '@/utils/functions/formatters/text-formatters';
import { PROGRAM_SECTION_VALIDATION_FUNCTIONS } from '@/validation/admin/program-schema/program-schema';
import { ReactComponent as PlusIcon } from '@/assets/icons/plus.svg';
import { EditableFaqCard } from './editable-faq-card/EditableFaqCard';
import styles from './FaqProgramSection.module.scss';
import viewStyles from './ViewFaqProgramSection.module.scss';

const { validateFaqQuestion, validateFaqAnswer } = PROGRAM_SECTION_VALIDATION_FUNCTIONS;

export interface FaqProgramSectionProps {
    questions?: PublishedFaqQuestion[];
    mode?: SectionMode;
    title?: string;
    onTitleChange?: (value: string) => void;
    faqPairs?: FaqSectionQuestionDto[];
    onFaqQuestionChange?: (index: number, value: string) => void;
    onFaqAnswerChange?: (index: number, value: string) => void;
    onAddFaqPair?: (questionText: string, answerText: string) => void;
    onDeleteFaqPair?: (index: number) => void;
    validationResetKey?: number;
    canAddFaqPair?: boolean;
}

const TEMPLATE = SectionTemplate.SingleTitleQuestionAnswerPairs;

export const FaqProgramSection = ({
    questions = [],
    mode = SectionMode.View,
    title,
    onTitleChange,
    faqPairs = [],
    onFaqQuestionChange,
    onFaqAnswerChange,
    onAddFaqPair,
    onDeleteFaqPair,
    validationResetKey,
    canAddFaqPair = true,
}: FaqProgramSectionProps) => {
    const { t } = useTranslation('programsPage');
    const idPrefix = useId();

    const displayTitle = title || t('COMMON_QUESTIONS');
    const isTemplate = mode === SectionMode.Template;
    const isEditable = mode === SectionMode.Edit;

    const titleMaxLength = getSectionTemplateMaxLength(TEMPLATE, ContentType.Title);

    const { titleError, handleTitleChange, handleTitleBlur } = useSectionValidation({
        template: TEMPLATE,
        onTitleChange,
        resetKey: validationResetKey,
    });

    const [expandedIndex, setExpandedIndex] = useState<number | null>(faqPairs.length === 1 ? 0 : null);
    const [autoFocusIndex, setAutoFocusIndex] = useState<number | null>(null);
    const initialPairAddedRef = useRef(false);

    useEffect(() => {
        if (isEditable && faqPairs.length === 0 && !initialPairAddedRef.current) {
            initialPairAddedRef.current = true;
            onAddFaqPair?.('', '');
            setExpandedIndex(0);
            setAutoFocusIndex(0);
        }
    }, [isEditable, faqPairs.length, onAddFaqPair]);

    const isAddDisabled =
        !canAddFaqPair ||
        (faqPairs.length > 0 &&
            faqPairs.some((pair) => !!validateFaqQuestion(pair.questionText) || !!validateFaqAnswer(pair.answerText)));

    const handleAddClick = useCallback(() => {
        onAddFaqPair?.('', '');
        const newIndex = faqPairs.length;
        setExpandedIndex(newIndex);
        setAutoFocusIndex(newIndex);
    }, [onAddFaqPair, faqPairs.length]);

    const handleExpandToggle = useCallback((index: number) => {
        setExpandedIndex((prev) => (prev === index ? null : index));
    }, []);

    const handleDelete = useCallback(
        (index: number) => {
            onDeleteFaqPair?.(index);
            setExpandedIndex((prev) => {
                if (prev === null) return null;
                if (prev === index) return null;
                if (prev > index) return prev - 1;
                return prev;
            });
            setAutoFocusIndex(null);
        },
        [onDeleteFaqPair],
    );

    const viewQuestions: PublishedFaqQuestion[] = useMemo(() => {
        if (questions.length > 0) return questions;

        return faqPairs.map((pair, index) => ({
            id: pair.id ?? -(index + 1),
            questionText: pair.questionText,
            answerText: pair.answerText,
        }));
    }, [questions, faqPairs]);

    const baseStyles = mode === SectionMode.View ? viewStyles : styles;

    const rootClassName = cn(baseStyles['faq-section'], {
        [styles['template']]: isTemplate,
        [styles['editable']]: isEditable,
    });

    if (isEditable) {
        return (
            <div className={rootClassName}>
                <div className={styles['faq-block']}>
                    <div className={styles['left-section']}>
                        <TextAreaWithCharacterLimitGroup
                            label={SECTIONS_TEXT.SECTION.FORM.TITLE.TEXT}
                            isRequired
                            id={`${idPrefix}-faq-title`}
                            name={`${idPrefix}-faq-title`}
                            value={title ?? ''}
                            onChange={handleTitleChange}
                            onBlur={handleTitleBlur}
                            maxLength={titleMaxLength}
                            rows={1}
                            autoGrow={true}
                            maxRows={8}
                            error={titleError}
                            currentLength={getTrimmedInputText(title ?? '').length}
                            className={styles['title-input-group']}
                            maxLimitWarning={COMMON_TEXT_ADMIN.VALIDATION_MESSAGE.getMaxError(titleMaxLength)}
                        />
                    </div>

                    <div className={styles['right-section']}>
                        <div className={styles['cards-list']}>
                            {faqPairs.map((pair, index) => (
                                <EditableFaqCard
                                    key={pair.id || index}
                                    index={index}
                                    idPrefix={idPrefix}
                                    questionText={pair.questionText}
                                    answerText={pair.answerText}
                                    isExpanded={expandedIndex === index}
                                    autoFocus={autoFocusIndex === index}
                                    canDelete={faqPairs.length > 1}
                                    onQuestionChange={(i, val) => onFaqQuestionChange?.(i, val)}
                                    onAnswerChange={(i, val) => onFaqAnswerChange?.(i, val)}
                                    onDelete={handleDelete}
                                    onExpandToggle={handleExpandToggle}
                                />
                            ))}
                        </div>

                        <div className={styles['actions-row']}>
                            <Button
                                buttonStyle="primary"
                                className={styles['add-button']}
                                onClick={handleAddClick}
                                type="button"
                                disabled={isAddDisabled}
                            >
                                {SECTIONS_TEXT.BUTTON.ADD}
                                <PlusIcon />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={rootClassName}>
            <div className={baseStyles['faq-block']}>
                <h2 className={baseStyles['title']}>{displayTitle}</h2>
                <div className={baseStyles['questions-container']}>
                    {viewQuestions.map((faq, index) => (
                        <FaqCard key={faq.id || index} faq={faq} className={baseStyles['faq-card-program-section']} />
                    ))}
                </div>
            </div>
        </div>
    );
};
